import { useEffect, useSyncExternalStore } from 'react';
import { api } from '../../api/client.js';
import { outbox } from '../../offline/index.js';

// Your messages, shared by the Messages screen, the unread badges and the
// acknowledgement reminder (one fetch for all of them). The last list is kept
// on the device so messages can be read with no signal. Opening one and
// "I acknowledge" go through the outbox like any other tap; until the server
// has them, this device remembers them so the inbox doesn't flip back.
const LIST_KEY = 'nu.inbox';
const MARKS_KEY = 'nu.inbox.marks';
const EVERY = 60_000;

const read = (k, fallback) => {
  try { return JSON.parse(localStorage.getItem(k)) ?? fallback; } catch { return fallback; }
};
const write = (k, v) => {
  try { localStorage.setItem(k, JSON.stringify(v)); } catch { /* lasts this visit */ }
};

let state = { messages: read(LIST_KEY, null), status: 'idle', error: null };
let marks = read(MARKS_KEY, {});
const listeners = new Set();
const emit = () => listeners.forEach((l) => l());
const set = (patch) => { state = { ...state, ...patch }; emit(); };

function withMarks(list) {
  if (!list) return list;
  return list.map((m) => {
    const k = marks[m.id];
    if (!k) return m;
    return { ...m, readAt: m.readAt || k.readAt || null, ackedAt: m.ackedAt || k.ackedAt || null };
  });
}

let view = { ...state, messages: withMarks(state.messages) };
let viewOf = null;
function snapshot() {
  if (viewOf !== state) {
    view = { ...state, messages: withMarks(state.messages) };
    viewOf = state;
  }
  return view;
}

let inFlight = null;
export function refreshInbox() {
  if (inFlight) return inFlight;
  if (state.status === 'idle') set({ status: 'loading' });
  inFlight = api('/messages')
    .then((list) => {
      // Marks the server now has can go.
      const next = {};
      for (const m of list) {
        const k = marks[m.id];
        if (k && ((k.readAt && !m.readAt) || (k.ackedAt && !m.ackedAt))) next[m.id] = k;
      }
      marks = next;
      write(MARKS_KEY, marks);
      write(LIST_KEY, list);
      set({ messages: list, status: 'ready', error: null });
    })
    .catch((e) => set({ status: state.messages ? 'ready' : 'error', error: e }))
    .finally(() => { inFlight = null; });
  return inFlight;
}

let timer = null;
function start() {
  if (timer) return;
  refreshInbox();
  timer = setInterval(() => { if (document.visibilityState === 'visible') refreshInbox(); }, EVERY);
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') refreshInbox(); });
  window.addEventListener('online', () => refreshInbox());
}

const subscribe = (l) => { listeners.add(l); return () => listeners.delete(l); };

export function useInbox() {
  useEffect(start, []);
  return useSyncExternalStore(subscribe, snapshot);
}

export const needsAck = (m) => m.requiresAck && !m.ackedAt;

export function useUnreadCount() {
  const { messages } = useInbox();
  return (messages || []).filter((m) => !m.readAt).length;
}

function mark(m, patch, type) {
  marks = { ...marks, [m.id]: { ...marks[m.id], ...patch } };
  write(MARKS_KEY, marks);
  state = { ...state }; // re-derive the view
  emit();
  return outbox.enqueue(type, { commId: m.id, at: Object.values(patch)[0] });
}

export function markRead(m) {
  if (m.readAt || marks[m.id]?.readAt) return undefined;
  return mark(m, { readAt: new Date().toISOString() }, 'comms.read');
}

export function acknowledge(m) {
  if (m.ackedAt) return undefined;
  const at = new Date().toISOString();
  return mark(m, { ackedAt: at, readAt: m.readAt || at }, 'comms.ack');
}

